function getBaseUrl() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location) {
    const { hostname } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:5000/api";
    }
    // Live on wibil.co (domain root)
    if (hostname.includes("wibil.co")) {
      return "/api";
    }
    // Legacy goldloanatm.com layout
    return "/backend/api";
  }
  return "http://localhost:5000/api";
}

const BASE_URL = getBaseUrl();

const SESSION_LIMIT_MS = 20 * 60 * 1000;
const SESSION_STARTED_KEY = "wibil_session_started";

function clearExpiredSessionAndRedirect() {
  try {
    sessionStorage.removeItem("wibil_token");
    sessionStorage.removeItem("wibil_user");
    sessionStorage.removeItem(SESSION_STARTED_KEY);
    localStorage.removeItem("wibil_token");
    localStorage.removeItem("wibil_user");
    localStorage.removeItem(SESSION_STARTED_KEY);
  } catch (e) {}
  if (typeof window !== "undefined" && window.location) {
    const path = String(window.location.pathname || "").toLowerCase();
    if (!path.includes("/login")) {
      const base = (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== ".")
        ? process.env.PUBLIC_URL.replace(/\/$/, "")
        : "";
      window.location.href = `${base}/login`;
    }
  }
}

function enforceClientSessionLimit() {
  try {
    const token = sessionStorage.getItem("wibil_token") || localStorage.getItem("wibil_token");
    if (!token) return;
    let startedRaw = sessionStorage.getItem(SESSION_STARTED_KEY) || localStorage.getItem(SESSION_STARTED_KEY);
    if (!startedRaw) {
      startedRaw = String(Date.now());
      sessionStorage.setItem(SESSION_STARTED_KEY, startedRaw);
      localStorage.setItem(SESSION_STARTED_KEY, startedRaw);
    }
    const started = Number(startedRaw);
    if (Number.isFinite(started) && Date.now() - started >= SESSION_LIMIT_MS) {
      clearExpiredSessionAndRedirect();
      throw new Error("Session expired. Please log in again.");
    }
  } catch (e) {
    if (e && e.message === "Session expired. Please log in again.") throw e;
  }
}

function currentUser() {
  try {
    const raw = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
    return raw && raw !== "undefined" ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function withUserParams(path) {
  const user = currentUser();
  const question = String(path).indexOf("?");
  const base = question >= 0 ? String(path).slice(0, question) : String(path);
  const params = new URLSearchParams(question >= 0 ? String(path).slice(question + 1) : "");
  if (user?.id && !params.has("userId")) params.set("userId", String(user.id));
  if (user?.role && !params.has("role") && !params.has("filter_role")) params.set("role", String(user.role));
  if (user?.email && !params.has("email")) params.set("email", String(user.email));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function authHeaders() {
  enforceClientSessionLimit();
  const token = sessionStorage.getItem("wibil_token") || localStorage.getItem("wibil_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handle(res) {
  const text = await res.text();
  let data = {};
  try {
    data = parseJsonPayload(text);
  } catch (e) {
    throw new Error("Invalid response from server.");
  }
  if (!res.ok) {
    if (data && data.duplicate) return data;
    throw new Error(data.error || data.message || "Something went wrong.");
  }
  return data;
}

function parseJsonPayload(text) {
  if (!text || !String(text).trim()) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    const src = String(text);
    const start = src.indexOf("{");
    if (start === -1) throw e;
    let depth = 0;
    let inStr = false;
    let esc = false;
    for (let i = start; i < src.length; i++) {
      const ch = src[i];
      if (inStr) {
        if (esc) {
          esc = false;
          continue;
        }
        if (ch === "\\") {
          esc = true;
          continue;
        }
        if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') {
        inStr = true;
        continue;
      }
      if (ch === "{") depth += 1;
      else if (ch === "}") {
        depth -= 1;
        if (depth === 0) {
          return JSON.parse(src.slice(start, i + 1));
        }
      }
    }
    throw e;
  }
}

export const api = {
  async post(path, body) {
    const user = currentUser();
    const payload = body && typeof body === "object" && !Array.isArray(body)
      ? { ...body }
      : body;
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      if (user?.id && payload.userId == null) payload.userId = user.id;
      if (user?.role && payload.role == null) payload.role = user.role;
    }
    const res = await fetch(`${BASE_URL}${withUserParams(path)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload ?? {}),
    });
    return handle(res);
  },
  async postForm(path, formData) {
    const user = currentUser();
    if (user?.id && !formData.has("userId")) formData.append("userId", String(user.id));
    if (user?.role && !formData.has("role")) formData.append("role", String(user.role));
    const res = await fetch(`${BASE_URL}${withUserParams(path)}`, {
      method: "POST",
      headers: { ...authHeaders() }, // browser sets multipart boundary
      body: formData,
    });
    return handle(res);
  },
  async get(path) {
    const res = await fetch(`${BASE_URL}${withUserParams(path)}`, { headers: { ...authHeaders() } });
    return handle(res);
  },
  async analyzeSocialProfile(body) {
    return this.post("/social-analyzer/analyze", body);
  },
  async getSocialJobProgress(searchId) {
    return this.get(`/social-analyzer/jobs/${searchId}`);
  },
  async getSocialCategories() {
    return this.get("/social-analyzer/categories");
  },
  async exportSocialReport(body) {
    return this.post("/social-analyzer/export", body);
  },
  async sendWhatsAppShortlist(body) {
    return this.post("/recruiter/send-whatsapp-shortlist", body);
  },
  async getLiveCandidates(query = "", platform = "all") {
    return this.get(`/recruiter/live-candidates?query=${encodeURIComponent(query)}&platform=${encodeURIComponent(platform)}`);
  },
  async getAppliedCandidates(search = "") {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    try {
      return await this.get(`/recruiter/applied-candidates${qs}`);
    } catch (err) {
      return this.get(`/recruiter/candidates?search=${encodeURIComponent(search)}&minScore=0`);
    }
  },
  async applyToJob({ jobId, employeeId } = {}) {
    return this.post("/employees/apply", { job_id: jobId, employee_id: employeeId });
  },
  async pullLinkedInCandidate(payload) {
    const body = typeof payload === "string" ? { profileUrl: payload } : (payload || {});
    return this.post("/recruiter/linkedin/pull", body);
  },
  async put(path, body) {
    const user = currentUser();
    const payload = body && typeof body === "object" && !Array.isArray(body)
      ? { ...body }
      : body;
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      if (user?.id && payload.userId == null) payload.userId = user.id;
      if (user?.role && payload.role == null) payload.role = user.role;
    }
    const res = await fetch(`${BASE_URL}${withUserParams(path)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload ?? {}),
    });
    return handle(res);
  },
  async listSourcedCandidates(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") qs.set(key, String(value).trim());
    });
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.get(`/candidates${suffix}`);
  },
  async getSourcedCandidate(id) {
    return this.get(`/candidates/${encodeURIComponent(id)}`);
  },
  async checkCandidateDuplicate(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") qs.set(key, String(value).trim());
    });
    return this.get(`/candidates/check-duplicate?${qs.toString()}`);
  },
  async saveLinkedInCandidate(payload) {
    return this.post("/candidates/linkedin", payload || {});
  },
  async saveGitHubCandidate(payload) {
    return this.post("/candidates/github", payload || {});
  },
  async updateSourcedCandidate(id, payload) {
    return this.put(`/candidates/${encodeURIComponent(id)}`, payload || {});
  },
  async deleteSourcedCandidate(id) {
    return this.del(`/candidates/${encodeURIComponent(id)}`);
  },
  async skillSearchCandidates(payload) {
    const body = typeof payload === "string" ? { headline: payload } : (payload || {});
    return this.post("/recruiter/skill-search", body);
  },
  async getRecruiterIntegrations() {
    return this.get("/recruiter/integrations");
  },
  async connectRecruiterIntegration(provider) {
    return this.post(`/recruiter/integrations/${provider}/connect`, {});
  },
  async syncRecruiterIntegration(provider) {
    return this.post(`/recruiter/integrations/${provider}/sync`, {});
  },
  async matchJobCandidates(jobId, jobData) {
    return this.post(`/recruiter/jobs/${jobId}/match`, jobData);
  },
  async getRankedJobCandidates(jobId, page = 1, pageSize = 25) {
    return this.get(`/recruiter/jobs/${jobId}/candidates?page=${page}&pageSize=${pageSize}`);
  },
  async exportCandidateToAts(candidateId, provider, candidateData) {
    return this.post(`/recruiter/candidates/${candidateId}/export`, { provider, candidateData });
  },
  async getHiringApplications() {
    return this.get("/hiring/applications");
  },
  async getJobBoards() {
    return this.get("/recruiter/job-boards");
  },
  async getRecruiterHiringRequests() {
    return this.get("/recruiter/hiring-requests");
  },
  async postHiringRequestJob(requestId, body = {}) {
    return this.post(`/recruiter/hiring-requests/${requestId}/post`, body);
  },
  async syndicateRecruiterJob(jobId, platforms) {
    return this.post(`/recruiter/jobs/${jobId}/syndicate`, { platforms });
  },
  async saveJobBoardLink(jobId, platform, externalUrl) {
    return this.post(`/recruiter/jobs/${jobId}/board-link`, { platform, externalUrl });
  },
  async createHiringRequest(body) {
    return this.post("/employers/hiring-requests", body);
  },
  async getEmployerHiringRequests() {
    return this.get("/employers/hiring-requests");
  },
  async getHiringRecruiters() {
    return this.get("/employers/hiring-recruiters");
  },
  async getPublicJob(slug) {
    return this.get(`/jobs/public/${encodeURIComponent(slug)}`);
  },
  async getSeekerApplications(employeeId) {
    return this.get(`/employees/${employeeId}/applications`);
  },
  async getOpenJobs() {
    return this.get("/employees/open-jobs");
  },
  async getJobSeekerLinkedInJobs({ title, location, skills, employeeId } = {}) {
    const params = new URLSearchParams();
    if (title) params.set("title", title);
    if (location) params.set("location", location);
    if (skills) params.set("skills", skills);
    if (employeeId) params.set("employeeId", String(employeeId));
    const qs = params.toString();
    return this.get(`/job-seeker/linkedin-jobs${qs ? `?${qs}` : ""}`);
  },
  async updateApplicationStage(appId, stage) {
    return this.patch(`/hiring/applications/${appId}/stage`, { stage });
  },
  async scheduleInterview(body) {
    return this.post("/hiring/interviews/schedule", body);
  },
  async generateOffer(body) {
    return this.post("/hiring/offers/generate", body);
  },
  async syncProfile(linksData) {
    return this.post("/employees/sync-profile", linksData);
  },
  async scrapePlatformData(platform, targetUrl) {
    return this.post("/employees/scrape-platform", { platform, targetUrl });
  },
  async faceMatchProfile(selfieB64) {
    return this.post("/verify/face-match-profile", { selfieB64 });
  },
  async verifyAadhaarQr(dataB64, contentType = "PNG", candidateName = "", extra = {}) {
    return this.post("/verify/aadhaar-qr", { data: dataB64, data_content_type: contentType, candidateName, ...extra });
  },
  async updateSeekerIdentity(employeeId, body) {
    return this.post(`/employees/${employeeId}/identity`, body);
  },
  async updateEmployerIdentity(employerId, body) {
    return this.post(`/employers/${employerId}/identity`, body);
  },
  async getEmployerWorkplaceSummary(company) {
    return this.get(`/employers/workplace-summary?company=${encodeURIComponent(String(company || "").trim())}`);
  },
  async checkAtsResume(body) {
    return this.post("/ai/ats-resume-check", body);
  },
  async getLinkedInAdsStatus() {
    return this.get(withUserParams("/linkedin-ads/status"));
  },
  async connectLinkedInAds() {
    const user = currentUser();
    return this.post("/linkedin-ads/connect", { userId: user?.id, role: user?.role });
  },
  async saveLinkedInAdsCredentials({ clientId, clientSecret, callbackUrl }) {
    return this.post("/linkedin-ads/credentials", { clientId, clientSecret, callbackUrl });
  },
  async saveLinkedInAdsToken(accessToken) {
    const user = currentUser();
    return this.post("/linkedin-ads/token", { accessToken, userId: user?.id, role: user?.role });
  },
  async exchangeLinkedInAdsCode(code, state) {
    const user = currentUser();
    return this.post("/linkedin-ads/exchange", { code, state, userId: user?.id, role: user?.role });
  },
  async disconnectLinkedInAds() {
    const user = currentUser();
    return this.post("/linkedin-ads/disconnect", { userId: user?.id, role: user?.role });
  },
  async getLinkedInAdAccounts(pageToken) {
    const params = new URLSearchParams();
    if (pageToken) params.set("pageToken", pageToken);
    const q = params.toString() ? `?${params}` : "";
    return this.get(withUserParams(`/linkedin-ads/accounts${q}`));
  },
  async getLinkedInAdCampaigns(accountId, status) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const q = params.toString() ? `?${params}` : "";
    return this.get(withUserParams(`/linkedin-ads/accounts/${encodeURIComponent(accountId)}/campaigns${q}`));
  },
  async getLinkedInCampaignAnalytics({ accountId, campaignId, start, end, timeGranularity, fields }) {
    const user = currentUser();
    return this.post("/linkedin-ads/analytics", {
      accountId,
      campaignId,
      start,
      end,
      timeGranularity,
      fields,
      userId: user?.id,
      role: user?.role
    });
  },
  async sourceCandidates(body) {
    return this.post("/recruiter/source-candidates", body);
  },
  async scrapeWebsiteWithAi(body) {
    return this.post("/recruiter/scrape/ai-website", body);
  },
  async scrapeIndeedJobs(body) {
    return this.post("/recruiter/scrape/indeed-jobs", body);
  },
  async getGoogleCseStatus() {
    return this.get("/recruiter/google-cse");
  },
  async saveGoogleCse(body) {
    return this.post("/recruiter/google-cse", body);
  },
  async scrapeLinkedInJobs(body) {
    return this.post("/recruiter/scrape/linkedin-jobs", body);
  },
  async scrapeCareerPages(body) {
    return this.post("/recruiter/scrape/career-pages", body);
  },
  async del(path, body) {
    const res = await fetch(`${BASE_URL}${withUserParams(path)}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: body ? JSON.stringify(body) : undefined,
    });
    return handle(res);
  },
};

export const fileUrl = (filename) =>
  filename ? `${BASE_URL.replace(/\/api$/, "")}/uploads/${filename}` : null;

export function privateProofUrl(filename) {
  if (!filename) return "";
  const safe = String(filename).replace(/^.*[\\/]/, "");
  return `${BASE_URL}${withUserParams(`/ratings/proof?file=${encodeURIComponent(safe)}`)}`;
}

export async function openSuperAdminProof(type, id) {
  const url = `${BASE_URL}${withUserParams(`/super-admin/ratings/${type}/${id}/proof`)}`;
  const res = await fetch(url, { headers: { ...authHeaders() } });
  if (!res.ok) {
    let message = "Could not open proof.";
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (e) {}
    throw new Error(message);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export async function openPrivateProof(filename) {
  const url = privateProofUrl(filename);
  if (!url) throw new Error("No proof file is available.");
  const res = await fetch(url, { headers: { ...authHeaders() } });
  if (!res.ok) {
    let message = "Private proof is only available after login.";
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (e) {}
    throw new Error(message);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export function recruiterCandidatePhotoUrl({ imageUrl, profileUrl } = {}) {
  const params = new URLSearchParams();
  if (imageUrl) params.set("u", imageUrl);
  else if (profileUrl) params.set("profile", profileUrl);
  else return "";
  return `${BASE_URL}/recruiter/candidate-photo?${params.toString()}`;
}

export default api;
